import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CategoryManagementModal from "../../../core/common/email-templates/CategoryManagementModal";
import EmailTemplatesHeader from "./components/EmailTemplatesHeader";
import EmailTemplatesFilters from "./components/EmailTemplatesFilters";
import EmailTemplatesTable from "./components/EmailTemplatesTable";
import TestSendModal from "./components/TestSendModal";

import { setActiveCompany } from "../../../core/data/redux/email-templates/slice";
import {
  deleteEmailTemplate,
  fetchEmailTemplates,
  updateEmailTemplate,
  fetchCategories,
} from "../../../core/data/redux/email-templates/thunks";
import {
  selectEmailTemplates,
  selectEmailTemplatesLoading,
  selectEmailTemplateCategories,
} from "../../../core/data/redux/email-templates/selectors";
import api from "../../../services/api";
import { notify } from "../../../services/toast.config";
import * as emailTemplateApi from "../../../core/data/redux/services/emailTemplateApi";
import { all_routes } from "../../router/all_routes";

import type { EmailTemplate } from "../../../core/data/redux/email-templates/types";
import type { AppDispatch } from "../../../core/data/redux/store";
import type { TemplateStatsByTemplateId } from "./components/EmailTemplatesTable";

const signature = () => localStorage.getItem("signature");

interface PageFilters {
  search: string;
  category?: string;
  status: "all" | "active" | "inactive";
  page: number;
  limit: number;
}

const INITIAL_FILTERS: PageFilters = {
  search: "",
  status: "all",
  page: 1,
  limit: 10,
};

const EmailTemplatesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const templates = useSelector(selectEmailTemplates);
  const loading = useSelector(selectEmailTemplatesLoading);
  const categories = useSelector(selectEmailTemplateCategories);
  const activeCompany = useSelector(
    (state: any) =>
      state.emailTemplates?.activeCompany ||
      state.recruitment?.activeCompany ||
      null,
  );

  const [filters, setFilters] = useState<PageFilters>(INITIAL_FILTERS);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [testSendTarget, setTestSendTarget] =
    useState<EmailTemplate | null>(null);

  const [companies, setCompanies] = useState<any[]>([]);
  const [companyOptions, setCompanyOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
  const [stats, setStats] = useState<TemplateStatsByTemplateId>({});
  const STATS_DAYS = 30;

  const activeCompanyIdRef = useRef<string | undefined>(activeCompany?.company_id);
  useEffect(() => {
    activeCompanyIdRef.current = activeCompany?.company_id;
  }, [activeCompany?.company_id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setIsCompaniesLoading(true);
        const response = await api.post("userService/getAllCompanyList", {
          signature: signature(),
        });
        if (cancelled) return;
        const list = response?.data?.data?.data || [];
        setCompanies(list);
        setCompanyOptions(
          list.map((c: any) => ({
            value: `${c.id}-${c.type}`,
            label: `${c.name} - ${c.type}`,
          })),
        );
        if (list.length > 0 && !activeCompanyIdRef.current) {
          const first = list[0];
          dispatch(
            setActiveCompany({
              company_id: String(first.id),
              company_type: first.type,
              company_name: first.name,
            }),
          );
        }
      } catch {
        if (!cancelled) notify.error("Failed to load companies");
      } finally {
        if (!cancelled) setIsCompaniesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    if (activeCompany?.company_id) {
      dispatch(fetchCategories());
    }
  }, [dispatch, activeCompany?.company_id, activeCompany?.company_type]);

  useEffect(() => {
    if (activeCompany?.company_id) {
      dispatch(
        fetchEmailTemplates(
          filters.category ? { category: filters.category } : undefined,
        ),
      );
    }
  }, [
    dispatch,
    activeCompany?.company_id,
    activeCompany?.company_type,
    filters.category,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadStats = async () => {
      if (!activeCompany?.company_id) return;
      try {
        const response = await emailTemplateApi.getEmailTemplateStats(
          activeCompany.company_id,
          activeCompany.company_type,
          STATS_DAYS,
        );
        if (cancelled || !response) return;
        const next: TemplateStatsByTemplateId = {};
        response.stats.forEach((row) => {
          next[row.template_id] = {
            delivered: row.delivered,
            failed: row.failed,
            total: row.total,
          };
        });
        setStats(next);
      } catch {
        if (!cancelled) setStats({});
      }
    };
    loadStats();
    return () => {
      cancelled = true;
    };
  }, [activeCompany?.company_id, activeCompany?.company_type]);

  const handleCompanyChange = useCallback(
    (companyKey: string) => {
      const [idPart, ...typeParts] = companyKey.split("-");
      const typePart = typeParts.join("-");
      const matched = companies.find(
        (c) => String(c.id) === idPart && c.type === typePart,
      );
      dispatch(
        setActiveCompany({
          company_id: idPart,
          company_type: typePart,
          company_name: matched?.name,
        }),
      );
      setFilters((prev) => ({ ...prev, page: 1 }));
    },
    [companies, dispatch],
  );

  const handleFilterChange = useCallback((partial: Partial<PageFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...partial,
      page:
        partial.page !== undefined
          ? partial.page
          : partial.limit !== undefined
            ? 1
            : "category" in partial ||
              "search" in partial ||
              "status" in partial
              ? 1
              : prev.page,
    }));
  }, []);

  const handleReset = useCallback(() => {
    setFilters(INITIAL_FILTERS);
  }, []);

  const openCreate = () => {
    navigate(all_routes.emailTemplateCreate);
  };

  const openEdit = (tpl: EmailTemplate) => {
    navigate(`/configurations/email-templates/${tpl.id}/edit`);
  };

  const openDuplicate = (tpl: EmailTemplate) => {
    navigate(all_routes.emailTemplateCreate, {
      state: { duplicateFrom: tpl },
    });
  };

  const handleDelete = useCallback(
    async (tpl: EmailTemplate) => {
      try {
        await dispatch(deleteEmailTemplate(tpl.id)).unwrap();
        notify.success("Template deleted");
      } catch (error: any) {
        notify.error(
          typeof error === "string" ? error : "Failed to delete template",
        );
      }
    },
    [dispatch],
  );

  const handleToggleActive = useCallback(
    async (tpl: EmailTemplate) => {
      try {
        await dispatch(
          updateEmailTemplate({
            templateId: tpl.id,
            template: { is_active: !tpl.is_active },
          }),
        ).unwrap();
        notify.success(
          tpl.is_active ? "Template deactivated" : "Template activated",
        );
        dispatch(
          fetchEmailTemplates(
            filters.category ? { category: filters.category } : undefined,
          ),
        );
      } catch (error: any) {
        notify.error(
          typeof error === "string" ? error : "Failed to update status",
        );
      }
    },
    [dispatch, filters.category],
  );

  const filteredTemplates = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return templates.filter((t) => {
      if (filters.category && t.category !== filters.category) return false;
      if (filters.status === "active" && !t.is_active) return false;
      if (filters.status === "inactive" && t.is_active) return false;
      if (q) {
        const hay = `${t.template_name} ${t.template_code} ${t.subject}`
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [templates, filters]);

  const paginatedTemplates = useMemo(() => {
    const start = (filters.page - 1) * filters.limit;
    return filteredTemplates.slice(start, start + filters.limit);
  }, [filteredTemplates, filters.page, filters.limit]);

  const pagination = useMemo(
    () => ({
      current_page: filters.page,
      per_page: filters.limit,
      total: filteredTemplates.length,
      total_pages: Math.max(1, Math.ceil(filteredTemplates.length / filters.limit)),
    }),
    [filteredTemplates.length, filters.page, filters.limit],
  );

  const activeCount = filteredTemplates.filter((t) => t.is_active).length;

  const activeCompanyKey = activeCompany?.company_id
    ? `${activeCompany.company_id}-${activeCompany.company_type}`
    : undefined;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <EmailTemplatesHeader
            companies={companyOptions}
            activeCompanyKey={activeCompanyKey}
            companiesLoading={isCompaniesLoading}
            onCompanyChange={handleCompanyChange}
            onManageCategories={() => setCategoryModalOpen(true)}
            onCreate={openCreate}
            createDisabled={!activeCompany?.company_id}
          />

          <EmailTemplatesFilters
            value={{
              search: filters.search,
              category: filters.category,
              status: filters.status,
            }}
            categories={categories}
            onChange={handleFilterChange}
            onReset={handleReset}
            totalCount={filteredTemplates.length}
            activeCount={activeCount}
          />

          <EmailTemplatesTable
            templates={paginatedTemplates}
            categories={categories}
            loading={loading}
            pagination={pagination}
            stats={stats}
            statsDays={STATS_DAYS}
            onPageChange={(page) => handleFilterChange({ page })}
            onLimitChange={(limit) => handleFilterChange({ limit, page: 1 })}
            onEdit={openEdit}
            onDuplicate={openDuplicate}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onTestSend={setTestSendTarget}
          />
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By <span className="text-primary">ZYVO</span>
          </p>
        </div>
      </div>

      <CategoryManagementModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
      />

      <TestSendModal
        open={Boolean(testSendTarget)}
        template={testSendTarget}
        companyId={activeCompany?.company_id}
        companyType={activeCompany?.company_type}
        onClose={() => setTestSendTarget(null)}
      />
    </>
  );
};

export default EmailTemplatesPage;
